import React, { useState } from 'react';
import styled from '@emotion/styled';
import Transition from 'react-transition-group/Transition';
import { connectScreenSize } from 'react-screen-size';
import { type, getBorders, getColor, spacing, getTextColor } from '../../style';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import { CustomStyledProps } from 'style/style';
import breakpoints from 'theme/material/breakpoints';

const DropArrow = styled('div')(
  {
    display: 'flex',
    alignItems: 'center',
  },
  ({ hasSelectedCount }: CustomStyledProps) => hasSelectedCount && { color: getColor().BRAND_2 }
);

const SelectedCount = styled('div')({
  fontSize: '14px',
});

const HeaderContainer = styled('div')<{ filterHeaderStyle?: React.CSSProperties; collapsable?: boolean }>(
  ({ collapsable, filterHeaderStyle }) => ({
    margin: '0',
    ...type.MD,
    fontWeight: 400,
    lineHeight: '23px',
    letterSpacing: '-0.5px',
    color: '#484848',
    display: 'flex',
    alignItems: 'center',
    textAlign: 'justify',
    paddingTop: 16,
    [breakpoints.up(640)]: {
      float: 'left',
      width: '25%',
      paddingTop: 10,
      fontWeight: 400,
      fontSize: 18,
    },
    paddingBottom: collapsable ? 16 : 6,
    [breakpoints.down(639.05)]: {
      cursor: collapsable ? 'pointer' : 'inherit',
    },
    ...filterHeaderStyle,
  })
);

const Header = styled('div')(() => ({
  display: 'inline-block',
  verticalAlign: 'middle',
  flexGrow: '1',
  ...getTextColor().DARKER,
}));

const ArrowContainer = styled('div')({
  marginLeft: '8px',
});

const ArrowSvg = styled('svg')(
  {
    display: 'block',
    fill: 'currentcolor',
    height: '10px',
    width: '10px',

    transitionProperty: 'transform',
    transitionDuration: '250ms',
    transitionTimingFunction: 'ease-in-out',
  },
  ({ arrowRotation }: CustomStyledProps) => ({ transform: `rotate(${arrowRotation}deg)` })
);

const FilterItem = styled('div')<{ itemsLen?: number }>(({ itemsLen }) => ({
  display: 'inline-block',
  width: '100%',

  [breakpoints.up(itemsLen > 3 ? 321 : 480)]: {
    width: '50%',
  },

  [breakpoints.up(769)]: {
    width: '33.333%',
  },
}));

const SectionContent = styled('div')({
  [breakpoints.up(640)]: {
    width: '75%',
    display: 'inline-block',
  },
});

const FilterGroup = styled('div')<{ filterGroupStyle?: React.CSSProperties }>(({ filterGroupStyle }) => ({
  display: 'inline-block',
  width: '100%',
  borderBottom: getBorders().DEFAULT,

  [breakpoints.up(640)]: {
    ...spacing.PADDING_TOP_MD,
    ...spacing.PADDING_BOTTOM_MD,
  },
  ...filterGroupStyle,
}));

const FilterBody = styled('div')(
  {
    opacity: 0,
    transition: 'transform 300ms, opacity 300ms',
    // overflow: 'hidden',
    height: 0,
    transform: 'translateY(-20px)',
  },
  ({ transitioned }: CustomStyledProps) => ({
    [breakpoints.down(639.05)]: {
      marginBottom: transitioned ? 16 : 0,
    },
  }),
  ({ status }: CustomStyledProps) => {
    if (status === 'entered') {
      return {
        height: 'auto',
        opacity: 1,
        transform: 'translateY(0px)',
        // overflow: 'auto'
      };
    }

    if (status === 'exiting') {
      return {
        height: 'auto',
        transform: 'translateY(-20px)',
        opacity: 0,
        // overflow: 'auto'
      };
    }
  }
);

const FilterRow = styled('div')<{ filterRightStyle?: React.CSSProperties }>(({ filterRightStyle }) => ({
  [breakpoints.down(639.05)]: {
    marginBottom: 16,
  },
  ...filterRightStyle,
}));

const FilterSection = ({
  fullWidth,
  collapsable,
  icon,
  header,
  numActive,
  hideSelectedText,
  filterGroupStyle,
  filterHeaderStyle,
  filterRightStyle,
  children,
}) => {
  const { isMobile } = useMediaBreakpoints();
  const [menuActive, setMenuActive] = useState(false);

  const toggleMenu = () => {
    setMenuActive(!menuActive);
  };

  const buildFilterItems = () => {
    //noCols: stops the iteration of the immediate children, just returns
    // all the children wrapped in a filterItem.

    const items = React.Children.map(children, child => {
      return <FilterItem itemsLen={children?.length}>{child}</FilterItem>;
    });

    return <SectionContent>{fullWidth ? children : items}</SectionContent>;
  };

  const arrowRotation = menuActive ? 180 : 0;

  const transitionedSection = (displayTransition, filterRightStyle) => {
    if (displayTransition === true) {
      return (
        <Transition
          in={menuActive}
          timeout={{
            enter: 0,
            exit: 300,
          }}
          mountOnEnter
          unmountOnExit
        >
          {status => (
            <FilterBody status={status} transitioned>
              {buildFilterItems()}
            </FilterBody>
          )}
        </Transition>
      );
    }
    return <FilterRow filterRightStyle={filterRightStyle}>{buildFilterItems()}</FilterRow>;
  };

  const renderDropArrow = () => {
    const selectedText = !hideSelectedText ? ' selected' : '';
    const currCount = numActive ? numActive + selectedText : '';

    return (
      <DropArrow hasSelectedCount={currCount}>
        <SelectedCount>{currCount}</SelectedCount>
        <ArrowContainer>
          <ArrowSvg
            viewBox="0 0 18 18"
            role="presentation"
            aria-hidden="true"
            focusable="false"
            arrowRotation={arrowRotation}
          >
            <path
              fillRule="evenodd"
              d="M16.291 4.295a1 1 0 1 1 1.414 1.415l-8 7.995a1 1 0 0 1-1.414 0l-8-7.995a1 1 0 1 1 1.414-1.415l7.293 7.29 7.293-7.29z"
            />
          </ArrowSvg>
        </ArrowContainer>
      </DropArrow>
    );
  };

  return (
    <FilterGroup filterGroupStyle={filterGroupStyle}>
      <HeaderContainer onClick={toggleMenu} collapsable={collapsable} filterHeaderStyle={filterHeaderStyle}>
        <Header>
          {icon && icon}
          {header}
        </Header>
        {collapsable && isMobile && renderDropArrow()}
      </HeaderContainer>
      {transitionedSection(collapsable && isMobile, filterRightStyle)}
    </FilterGroup>
  );
};

const mapScreenSizeToProps = screenSize => screenSize;

export default connectScreenSize(mapScreenSizeToProps)(FilterSection);
